/**
 * Time tracking hooks using TanStack Query
 * Provides data fetching, caching, and mutations for time tracking operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useAuthContext } from '@/contexts/AuthContext';
import { ApiError, TimeEntriesService } from '@/api';
import { toast } from 'sonner';
import type { I18n } from '@lingui/core';
import { useLingui } from '@lingui/react';
import type {
  TimeEntry,
  TimeEntryListResponse,
  TimesheetSummary,
  BulkStatusResponse,
  BreakEntry,
  ClockInRequest,
  ClockOutRequest,
  StartBreakRequest,
  EndBreakRequest,
  CreateManualEntryRequest,
  UpdateTimeEntryRequest,
  SubmitForApprovalRequest,
  ApproveTimeEntriesRequest,
  RejectTimeEntriesRequest,
  CreateBreakEntryRequest,
  UpdateBreakEntryRequest,
  TimeEntryListParams,
  TimesheetParams,
} from '@/types/timeTracking';
import { NAV_BADGE_POLL_MS, visiblePoll } from '@/lib/polling';

// Query keys for time tracking
export const timeTrackingKeys = {
  all: ['time-entries'] as const,
  lists: () => [...timeTrackingKeys.all, 'list'] as const,
  list: (params: TimeEntryListParams) =>
    [...timeTrackingKeys.lists(), params] as const,
  details: () => [...timeTrackingKeys.all, 'detail'] as const,
  detail: (id: string) => [...timeTrackingKeys.details(), id] as const,
  timesheets: () => [...timeTrackingKeys.all, 'timesheet'] as const,
  timesheet: (params: TimesheetParams) =>
    [...timeTrackingKeys.timesheets(), params] as const,
};

/**
 * Error handler for time tracking mutations.
 * Maps API errors to user-friendly toast messages.
 */
function handleTimeTrackingError(
  error: Error,
  i18n: I18n,
  context: 'clockIn' | 'clockOut' | 'break' | 'create' | 'update' | 'delete' | 'submit' | 'approve' | 'reject'
): void {
  const lowerMessage = (error.message || '').toLowerCase();

  if (error instanceof ApiError) {
    if (error.status === 409) {
      if (lowerMessage.includes('already clocked in')) {
        showTimeTrackingToast(i18n, 'alreadyClockedIn');
        return;
      }
      if (lowerMessage.includes('not currently clocked in')) {
        showTimeTrackingToast(i18n, 'notClockedIn');
        return;
      }
      if (lowerMessage.includes('break is already in progress')) {
        showTimeTrackingToast(i18n, 'breakAlreadyActive');
        return;
      }
      if (lowerMessage.includes('no active break')) {
        showTimeTrackingToast(i18n, 'noActiveBreak');
        return;
      }
      if (
        lowerMessage.includes('modified by another user') ||
        lowerMessage.includes('optimistic')
      ) {
        showTimeTrackingToast(i18n, 'optimisticLock');
        return;
      }
      if (lowerMessage.includes('not in open status')) {
        showTimeTrackingToast(i18n, 'notOpen');
        return;
      }
      showTimeTrackingToast(i18n, 'conflict');
      return;
    }

    if (error.status === 404) {
      if (lowerMessage.includes('no entries found')) {
        showTimeTrackingToast(i18n, 'noEntries');
        return;
      }
      showTimeTrackingToast(i18n, 'notFound');
      return;
    }

    if (error.status === 400) {
      if (lowerMessage.includes('clock out must be after')) {
        showTimeTrackingToast(i18n, 'invalidClockTimes');
        return;
      }
      if (lowerMessage.includes('break end must be after')) {
        showTimeTrackingToast(i18n, 'invalidBreakTimes');
        return;
      }
      if (lowerMessage.includes('break times must be within')) {
        showTimeTrackingToast(i18n, 'breakOutsideEntry');
        return;
      }
    }
  }

  // Fallback based on context
  switch (context) {
    case 'clockIn':
      showTimeTrackingToast(i18n, 'clockInError');
      break;
    case 'clockOut':
      showTimeTrackingToast(i18n, 'clockOutError');
      break;
    case 'break':
      showTimeTrackingToast(i18n, 'breakError');
      break;
    case 'delete':
      showTimeTrackingToast(i18n, 'deleteError');
      break;
    default:
      showTimeTrackingToast(i18n, 'error');
      break;
  }
}

type TimeTrackingToastId =
  | 'alreadyClockedIn'
  | 'notClockedIn'
  | 'breakAlreadyActive'
  | 'noActiveBreak'
  | 'optimisticLock'
  | 'notOpen'
  | 'conflict'
  | 'notFound'
  | 'noEntries'
  | 'invalidClockTimes'
  | 'invalidBreakTimes'
  | 'breakOutsideEntry'
  | 'clockInError'
  | 'clockOutError'
  | 'breakError'
  | 'deleteError'
  | 'error';

function showTimeTrackingToast(i18n: I18n, errorType: TimeTrackingToastId): void {
  let message: string;

  switch (errorType) {
    case 'alreadyClockedIn':
      message = i18n._({
        id: 'timeTracking.toast.alreadyClockedIn',
        message: 'Employee is already clocked in',
      });
      break;
    case 'notClockedIn':
      message = i18n._({
        id: 'timeTracking.toast.notClockedIn',
        message: 'Employee is not currently clocked in',
      });
      break;
    case 'breakAlreadyActive':
      message = i18n._({
        id: 'timeTracking.toast.breakAlreadyActive',
        message: 'A break is already in progress',
      });
      break;
    case 'noActiveBreak':
      message = i18n._({
        id: 'timeTracking.toast.noActiveBreak',
        message: 'No active break in progress',
      });
      break;
    case 'optimisticLock':
      message = i18n._({
        id: 'timeTracking.toast.optimisticLock',
        message: 'Time entry was modified by another user. Please refresh.',
      });
      break;
    case 'notOpen':
      message = i18n._({
        id: 'timeTracking.toast.notOpen',
        message: 'Time entry is not in OPEN status',
      });
      break;
    case 'conflict':
      message = i18n._({
        id: 'timeTracking.toast.conflict',
        message: 'A conflict occurred with this time entry',
      });
      break;
    case 'notFound':
      message = i18n._({
        id: 'timeTracking.toast.notFound',
        message: 'Time entry not found',
      });
      break;
    case 'noEntries':
      message = i18n._({
        id: 'timeTracking.toast.noEntries',
        message: 'No entries found for the specified range',
      });
      break;
    case 'invalidClockTimes':
      message = i18n._({
        id: 'timeTracking.toast.invalidClockTimes',
        message: 'Clock out must be after clock in',
      });
      break;
    case 'invalidBreakTimes':
      message = i18n._({
        id: 'timeTracking.toast.invalidBreakTimes',
        message: 'Break end must be after break start',
      });
      break;
    case 'breakOutsideEntry':
      message = i18n._({
        id: 'timeTracking.toast.breakOutsideEntry',
        message: 'Break times must be within clock-in/clock-out range',
      });
      break;
    case 'clockInError':
      message = i18n._({
        id: 'timeTracking.toast.clockInError',
        message: 'Failed to clock in',
      });
      break;
    case 'clockOutError':
      message = i18n._({
        id: 'timeTracking.toast.clockOutError',
        message: 'Failed to clock out',
      });
      break;
    case 'breakError':
      message = i18n._({
        id: 'timeTracking.toast.breakError',
        message: 'Failed to perform break operation',
      });
      break;
    case 'deleteError':
      message = i18n._({
        id: 'timeTracking.toast.deleteError',
        message: 'Failed to delete time entry',
      });
      break;
    case 'error':
    default:
      message = i18n._({
        id: 'timeTracking.toast.error',
        message: 'Error performing time tracking operation',
      });
      break;
  }

  toast.error(message);
}

// =====================================================
// Query hooks
// =====================================================

/**
 * Hook to fetch paginated time entries with filters.
 */
export function useTimeEntries(params: TimeEntryListParams = {}) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery<TimeEntryListResponse>({
    queryKey: timeTrackingKeys.list(params),
    queryFn: () =>
      TimeEntriesService.getApiV1TimeEntries(
        params.employeeId,
        params.fromDate,
        params.toDate,
        params.status as 'OPEN' | 'SUBMITTED' | 'APPROVED' | 'REJECTED' | undefined,
        params.limit,
        params.offset
      ),
    enabled: isSignedIn && isAuthReady,
    staleTime: 30000,
    retry: (failureCount, error) => {
      if (error instanceof ApiError) {
        if (error.status === 401 || error.status === 403) {
          return false;
        }
      }
      return failureCount < 3;
    },
  });
}

/**
 * Hook to count time entries waiting for approval (`status=SUBMITTED`), for the
 * nav badge. `limit=1` keeps the payload to one row; `total_count` is the count.
 * Keyed under `lists()` so the approve/reject invalidations refresh it.
 */
export function useSubmittedTimeEntriesCount(options?: { enabled?: boolean }) {
  const { isSignedIn } = useAuth();
  const { isAuthReady, orgId } = useAuthContext();

  return useQuery({
    queryKey: [...timeTrackingKeys.lists(), 'submittedCount', orgId ?? null] as const,
    queryFn: () =>
      TimeEntriesService.getApiV1TimeEntries(undefined, undefined, undefined, 'SUBMITTED', 1, 0),
    select: (data) => data.total_count,
    enabled: (options?.enabled ?? true) && isSignedIn && isAuthReady,
    staleTime: 15000,
    ...visiblePoll(NAV_BADGE_POLL_MS),
    retry: (failureCount, error) => {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

/**
 * Hook to count the employees clocked in right now (an open entry), for the
 * time tracking switch on the settings page (#1954). The route is admin-only,
 * so the caller gates `enabled` on the admin role. Keyed under `all`, so a clock
 * mutation refreshes it.
 */
export function useClockedInCount(options: { enabled: boolean }) {
  const { isSignedIn } = useAuth();
  const { isAuthReady, orgId } = useAuthContext();

  return useQuery({
    queryKey: [...timeTrackingKeys.all, 'clockedInCount', orgId ?? null] as const,
    queryFn: () => TimeEntriesService.getApiV1TimeEntriesClockedInCount(),
    select: (data) => data.count,
    enabled: options.enabled && isSignedIn && isAuthReady,
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook to fetch a single time entry by ID.
 */
export function useTimeEntry(id: string, options?: { enabled?: boolean }) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery<TimeEntry>({
    queryKey: timeTrackingKeys.detail(id),
    queryFn: () => TimeEntriesService.getApiV1TimeEntries1(id),
    enabled: (options?.enabled ?? true) && !!id && isSignedIn && isAuthReady,
    staleTime: 30000,
  });
}

/**
 * Hook to fetch timesheet summary for an employee.
 */
export function useTimesheetSummary(
  params: TimesheetParams,
  options?: { enabled?: boolean }
) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery<TimesheetSummary>({
    queryKey: timeTrackingKeys.timesheet(params),
    queryFn: () =>
      TimeEntriesService.getApiV1TimeEntriesTimesheet(
        params.employeeId,
        params.fromDate,
        params.toDate
      ),
    enabled:
      (options?.enabled ?? true) &&
      !!params.employeeId &&
      !!params.fromDate &&
      !!params.toDate &&
      isSignedIn &&
      isAuthReady,
    staleTime: 30000,
  });
}

// =====================================================
// Mutation hooks
// =====================================================

/**
 * Hook to clock in an employee.
 */
export function useClockIn() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation<TimeEntry, Error, ClockInRequest>({
    mutationFn: (request) =>
      TimeEntriesService.postApiV1TimeEntriesClockIn(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: timeTrackingKeys.lists() });
      queryClient.invalidateQueries({ queryKey: timeTrackingKeys.timesheets() });
      toast.success(
        i18n._({
          id: 'timeTracking.toast.clockInSuccess',
          message: 'Clocked in successfully',
        })
      );
    },
    onError: (error: Error) => handleTimeTrackingError(error, i18n, 'clockIn'),
  });
}

/**
 * Hook to clock out an employee.
 */
export function useClockOut() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation<TimeEntry, Error, ClockOutRequest>({
    mutationFn: (request) =>
      TimeEntriesService.postApiV1TimeEntriesClockOut(request),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: timeTrackingKeys.lists() });
      queryClient.invalidateQueries({ queryKey: timeTrackingKeys.timesheets() });
      queryClient.invalidateQueries({
        queryKey: timeTrackingKeys.detail(variables.entry_id),
      });
      toast.success(
        i18n._({
          id: 'timeTracking.toast.clockOutSuccess',
          message: 'Clocked out successfully',
        })
      );
    },
    onError: (error: Error) => handleTimeTrackingError(error, i18n, 'clockOut'),
  });
}

/**
 * Hook to start a break.
 */
export function useStartBreak() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation<BreakEntry, Error, StartBreakRequest>({
    mutationFn: (request) =>
      TimeEntriesService.postApiV1TimeEntriesBreakStart(request),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: timeTrackingKeys.lists() });
      queryClient.invalidateQueries({ queryKey: timeTrackingKeys.timesheets() });
      queryClient.invalidateQueries({
        queryKey: timeTrackingKeys.detail(variables.entry_id),
      });
      toast.success(
        i18n._({
          id: 'timeTracking.toast.startBreakSuccess',
          message: 'Break started',
        })
      );
    },
    onError: (error: Error) => handleTimeTrackingError(error, i18n, 'break'),
  });
}

/**
 * Hook to end a break.
 */
export function useEndBreak() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation<BreakEntry, Error, EndBreakRequest>({
    mutationFn: (request) =>
      TimeEntriesService.postApiV1TimeEntriesBreakEnd(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: timeTrackingKeys.lists() });
      queryClient.invalidateQueries({ queryKey: timeTrackingKeys.timesheets() });
      toast.success(
        i18n._({
          id: 'timeTracking.toast.endBreakSuccess',
          message: 'Break ended',
        })
      );
    },
    onError: (error: Error) => handleTimeTrackingError(error, i18n, 'break'),
  });
}

/**
 * Hook to create a manual time entry.
 */
export function useCreateManualEntry() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation<TimeEntry, Error, CreateManualEntryRequest>({
    mutationFn: (request) =>
      TimeEntriesService.postApiV1TimeEntries(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: timeTrackingKeys.lists() });
      queryClient.invalidateQueries({ queryKey: timeTrackingKeys.timesheets() });
      toast.success(
        i18n._({
          id: 'timeTracking.toast.createSuccess',
          message: 'Time entry created successfully',
        })
      );
    },
    onError: (error: Error) => handleTimeTrackingError(error, i18n, 'create'),
  });
}

/**
 * Hook to update a time entry.
 */
export function useUpdateTimeEntry() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: ({
      id,
      request,
    }: {
      id: string;
      request: UpdateTimeEntryRequest;
    }) => TimeEntriesService.putApiV1TimeEntries(id, request),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: timeTrackingKeys.lists() });
      queryClient.invalidateQueries({ queryKey: timeTrackingKeys.timesheets() });
      queryClient.setQueryData(timeTrackingKeys.detail(variables.id), data);
      toast.success(
        i18n._({
          id: 'timeTracking.toast.updateSuccess',
          message: 'Time entry updated successfully',
        })
      );
    },
    onError: (error: Error) => handleTimeTrackingError(error, i18n, 'update'),
  });
}

/**
 * Hook to delete a time entry.
 */
export function useDeleteTimeEntry() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: (id: string) =>
      TimeEntriesService.deleteApiV1TimeEntries(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: timeTrackingKeys.lists() });
      queryClient.invalidateQueries({ queryKey: timeTrackingKeys.timesheets() });
      queryClient.removeQueries({ queryKey: timeTrackingKeys.detail(id) });
      toast.success(
        i18n._({
          id: 'timeTracking.toast.deleteSuccess',
          message: 'Time entry deleted successfully',
        })
      );
    },
    onError: (error: Error) => handleTimeTrackingError(error, i18n, 'delete'),
  });
}

/**
 * Hook to submit time entries for approval.
 */
export function useSubmitForApproval(options?: {
  /** No success toast: the timesheet page submits once per employee and reports once. */
  silent?: boolean;
}) {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();
  const silent = options?.silent ?? false;

  return useMutation<BulkStatusResponse, Error, SubmitForApprovalRequest>({
    mutationFn: (request) =>
      TimeEntriesService.postApiV1TimeEntriesSubmit(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: timeTrackingKeys.lists() });
      queryClient.invalidateQueries({ queryKey: timeTrackingKeys.timesheets() });
      if (!silent) {
        toast.success(
          i18n._({
            id: 'timeTracking.toast.submitSuccess',
            message: 'Timesheet submitted for approval',
          })
        );
      }
    },
    onError: (error: Error) => handleTimeTrackingError(error, i18n, 'submit'),
  });
}

/**
 * Hook to approve time entries.
 */
export function useApproveTimeEntries() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation<BulkStatusResponse, Error, ApproveTimeEntriesRequest>({
    mutationFn: (request) =>
      TimeEntriesService.postApiV1TimeEntriesApprove(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: timeTrackingKeys.lists() });
      queryClient.invalidateQueries({ queryKey: timeTrackingKeys.timesheets() });
      toast.success(
        i18n._({
          id: 'timeTracking.toast.approveSuccess',
          message: 'Timesheet approved',
        })
      );
    },
    onError: (error: Error) => handleTimeTrackingError(error, i18n, 'approve'),
  });
}

/**
 * Hook to reject time entries.
 */
export function useRejectTimeEntries() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation<BulkStatusResponse, Error, RejectTimeEntriesRequest>({
    mutationFn: (request) =>
      TimeEntriesService.postApiV1TimeEntriesReject(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: timeTrackingKeys.lists() });
      queryClient.invalidateQueries({ queryKey: timeTrackingKeys.timesheets() });
      toast.success(
        i18n._({
          id: 'timeTracking.toast.rejectSuccess',
          message: 'Timesheet rejected',
        })
      );
    },
    onError: (error: Error) => handleTimeTrackingError(error, i18n, 'reject'),
  });
}

// =====================================================
// Break entry mutation hooks (admin)
// =====================================================

/**
 * Hook to create a break entry for a time entry.
 */
export function useCreateBreakEntry() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation<
    BreakEntry,
    Error,
    { timeEntryId: string; request: CreateBreakEntryRequest }
  >({
    mutationFn: ({ timeEntryId, request }) =>
      TimeEntriesService.postApiV1TimeEntriesBreaks(timeEntryId, request),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: timeTrackingKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: timeTrackingKeys.timesheets(),
      });
      queryClient.invalidateQueries({
        queryKey: timeTrackingKeys.detail(variables.timeEntryId),
      });
      toast.success(
        i18n._({
          id: 'timeTracking.toast.breakCreateSuccess',
          message: 'Break added',
        })
      );
    },
    onError: (error: Error) => handleTimeTrackingError(error, i18n, 'break'),
  });
}

/**
 * Hook to update a break entry.
 */
export function useUpdateBreakEntry() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation<
    BreakEntry,
    Error,
    {
      timeEntryId: string;
      breakId: string;
      request: UpdateBreakEntryRequest;
    }
  >({
    mutationFn: ({ timeEntryId, breakId, request }) =>
      TimeEntriesService.putApiV1TimeEntriesBreaks(
        timeEntryId,
        breakId,
        request
      ),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: timeTrackingKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: timeTrackingKeys.timesheets(),
      });
      queryClient.invalidateQueries({
        queryKey: timeTrackingKeys.detail(variables.timeEntryId),
      });
      toast.success(
        i18n._({
          id: 'timeTracking.toast.breakUpdateSuccess',
          message: 'Break updated',
        })
      );
    },
    onError: (error: Error) => handleTimeTrackingError(error, i18n, 'break'),
  });
}

/**
 * Hook to delete a break entry.
 */
export function useDeleteBreakEntry() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation<
    void,
    Error,
    { timeEntryId: string; breakId: string }
  >({
    mutationFn: ({ timeEntryId, breakId }) =>
      TimeEntriesService.deleteApiV1TimeEntriesBreaks(timeEntryId, breakId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: timeTrackingKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: timeTrackingKeys.timesheets(),
      });
      queryClient.invalidateQueries({
        queryKey: timeTrackingKeys.detail(variables.timeEntryId),
      });
      toast.success(
        i18n._({
          id: 'timeTracking.toast.breakDeleteSuccess',
          message: 'Break removed',
        })
      );
    },
    onError: (error: Error) => handleTimeTrackingError(error, i18n, 'break'),
  });
}
